import React, { useState } from 'react';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { ProductApi, DinCollectionMethod } from 'common/types';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormHelperText from '@mui/material/FormHelperText';
import ProductDialog from 'components/ProductDialog';
import { useTranslation } from 'services/localization/useTranslation';
import ContainerBox from 'components/ContainerBox';
import { DinOptions, getSelectedBootProductIds, getSelectedSkuProductIds } from './utils';

interface Props {
	products: ProductApi[];
	dinOptions: DinOptions;
	setDinOptions: (dinOptions: DinOptions) => void;
}

const DinSettings = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const { products, dinOptions } = props;
	const { dinCollectionMethod, useDinVariation } = dinOptions;
	const [skiProductsDialogOpen, setSkiProductsDialogOpen] = useState<boolean>(false);
	const [bootProductsDialogOpen, setBootProductsDialogOpen] = useState<boolean>(false);
	const selectedSkiProductIds = getSelectedSkuProductIds(products, dinOptions);
	const selectedBootProductIds = getSelectedBootProductIds(products, dinOptions);

	const handleDinCollectionMethodChange = (value: DinCollectionMethod) => {
		props.setDinOptions({
			...dinOptions,
			dinCollectionMethod: value,
		});
	};

	const getNewProductsToUpdate = (
		idsToRemove: string[],
		idsToAdd: string[],
		fieldToUpdate: 'din' | 'soleLength',
	) => {
		let currentProductDinUpdates = dinOptions.productDinUpdates;
		for (const removedId of idsToRemove) {
			currentProductDinUpdates[removedId] = {
				type: 'remove',
				field: fieldToUpdate,
			};
		}
		for (const addedId of idsToAdd) {
			currentProductDinUpdates[addedId] = {
				type: 'add',
				field: fieldToUpdate,
			};
		}
		return currentProductDinUpdates;
	};

	const handleSkiProductsDialogSave = (newSelectedProductIds: string[]) => {
		const removedIds = selectedSkiProductIds.filter((id) => !newSelectedProductIds.includes(id));
		const addedIds = newSelectedProductIds.filter((id) => !selectedSkiProductIds.includes(id));
		const newProductsDinUpdates = getNewProductsToUpdate(removedIds, addedIds, 'din');
		props.setDinOptions({
			...dinOptions,
			productDinUpdates: newProductsDinUpdates,
		});
		setSkiProductsDialogOpen(false);
	};

	const handleSkiProductsDialogClose = () => {
		setSkiProductsDialogOpen(false);
	};

	const handleBootProductsDialogSave = (newSelectedProductIds: string[]) => {
		const removedIds = selectedBootProductIds.filter((id) => !newSelectedProductIds.includes(id));
		const addedIds = newSelectedProductIds.filter((id) => !selectedBootProductIds.includes(id));
		const newProductsDinUpdates = getNewProductsToUpdate(removedIds, addedIds, 'soleLength');
		props.setDinOptions({
			...dinOptions,
			productDinUpdates: newProductsDinUpdates,
		});
		setBootProductsDialogOpen(false);
	};

	const handleBootProductsDialogClose = () => {
		setBootProductsDialogOpen(false);
	};

	const setUseDinVariation = (value: boolean) => {
		props.setDinOptions({
			...dinOptions,
			useDinVariation: value,
		});
	};

	return (
		<ContainerBox>
			<Typography variant="h5">
				{t('productSettings.skiProductSettings', 'Settings for ski equipment renting')}
			</Typography>
			<Grid
				container
				direction="row"
				justifyContent="space-between"
				alignItems="center"
				className={classes.verticalSpacing}
			>
				<Typography variant="h6" gutterBottom>
					{t('productSettings.din.header', 'Mark DIN values for ski products')}
				</Typography>
				<Switch
					color="primary"
					checked={!!dinCollectionMethod}
					onChange={() => handleDinCollectionMethodChange(dinCollectionMethod ? null : 'manual')}
					value={'dinCollectionMethod'}
				/>
			</Grid>
			{dinCollectionMethod && (
				<>
					<FormControl component="fieldset" className={classes.verticalSpacing}>
						<FormLabel component="legend">
							{t('productSettings.din.header', 'Mark DIN values for ski products')}
						</FormLabel>
						<RadioGroup
							aria-label="dinCollectionMethod"
							name="dinCollectionMethod"
							value={dinCollectionMethod}
							onChange={(e) =>
								handleDinCollectionMethodChange(e.target.value as DinCollectionMethod)
							}
						>
							<FormControlLabel
								value="manual"
								control={<Radio />}
								label={t('productSettings.din.manualInput', 'Input DIN manually')}
							/>
							<FormControlLabel
								value="automatic"
								control={<Radio />}
								label={t(
									'productSettings.din.automaticCalculcation',
									'Use automatic DIN calculation',
								)}
							/>
						</RadioGroup>
						<FormHelperText color="textSecondary">
							{t(
								'productSettings.din.automaticCalculationInfo',
								'Automatic DIN calculation requires collecting boot sole length, and also asks for user experience, weight, height and age when renting ski products',
							)}
						</FormHelperText>
					</FormControl>
					<div className={classes.verticalSpacing}>
						<Typography variant="h6" component="div">
							{t(
								'productSettings.din.selectSkiProducts',
								'What are your ski products to collect DIN from',
							)}
							?
						</Typography>
						{dinCollectionMethod === 'automatic' ? (
							<Typography variant="caption" color="textSecondary" gutterBottom>
								{t(
									'productSettings.din.skiProductsInfo',
									'These products will get an extra column to their inventory, where the DIN formula for each item can be specified',
								)}
							</Typography>
						) : null}
						<div>
							<Button
								variant="outlined"
								color="primary"
								onClick={() => setSkiProductsDialogOpen(true)}
							>
								{selectedSkiProductIds.length
									? `${selectedSkiProductIds.length} ${t(
											'common:states.selected',
											'Selected',
									  ).toLowerCase()}`
									: t('common:actions.select').toLowerCase()}
							</Button>
						</div>
						{dinCollectionMethod === 'automatic' ? (
							<FormGroup>
								<FormControlLabel
									style={{ alignItems: 'center' }}
									control={
										<Checkbox
											color="primary"
											checked={useDinVariation}
											onChange={(e) => setUseDinVariation(e.target.checked)}
											value="dinVariation"
										/>
									}
									label={t('productSettings.din.skiVariationLabel', 'Use din variation in skis')}
								/>
								<FormHelperText>
									{t(
										'productSettings.din.skiVariationInfo',
										'When toggled, all the ski products in the inventory will get an extra din variation column to their inventory, where the DIN variantion can be specified. The variation will be added/subtracted from the calculated DIN value. F.ex. Skis with calculated DIN 6 and DIn variation 0.5 will show DIN value 6.5',
									)}
								</FormHelperText>
							</FormGroup>
						) : null}
						{skiProductsDialogOpen && (
							<ProductDialog
								classes={{
									paper: classes.productDialogPaper,
								}}
								open={skiProductsDialogOpen}
								onCancel={handleSkiProductsDialogClose}
								onSave={handleSkiProductsDialogSave}
								products={(products || []).filter((p) => !p.set)}
								activeProducts={selectedSkiProductIds}
							/>
						)}
					</div>
					<div className={classes.verticalSpacing}>
						<Typography variant="h6" gutterBottom component="div">
							{t(
								'productSettings.din.selectBootProducts',
								'What are your ski boot products used for calculating DIN',
							)}
							?
						</Typography>
						<Typography variant="caption" gutterBottom color="textSecondary">
							{t(
								'productSettings.din.bootProductsInfo',
								'We need to know all your ski boots and their sole lengths, so we can calculate the DIN values for you automatically. These products will get an extra sole length column to their inventory, where the sole length of each item can be specified',
							)}
						</Typography>
						<div>
							<Button
								variant="outlined"
								color="primary"
								onClick={() => setBootProductsDialogOpen(true)}
							>
								{selectedBootProductIds.length
									? `${selectedBootProductIds.length} ${t(
											'common:states.selected',
											'selected',
									  ).toLowerCase()}`
									: t('common:actions.select').toLowerCase()}
							</Button>
						</div>
						{bootProductsDialogOpen && (
							<ProductDialog
								classes={{
									paper: classes.productDialogPaper,
								}}
								open={bootProductsDialogOpen}
								onCancel={handleBootProductsDialogClose}
								onSave={handleBootProductsDialogSave}
								products={(products || []).filter((p) => !p.set)}
								activeProducts={selectedBootProductIds}
							/>
						)}
					</div>
				</>
			)}
			{dinCollectionMethod === 'automatic' && (
				<Typography variant="caption">
					{t(
						'productSettings.din.automaticCalculationNotice',
						`Rentle's automatic DIN calculation uses a standard DIN calculation chart. It is the responsibility of the shop to ensure that the values given for the DIN calculation are correct. The results from calculation are for reference based on the given values,and Rentle takes no responsibility of the binding settings of the skis that have been adjusted based on the calculations.`,
					)}
				</Typography>
			)}
		</ContainerBox>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {},
		textField: {
			marginLeft: theme.spacing(1),
			marginRight: theme.spacing(1),
			maxWidth: 380,
			width: `calc(100% - ${theme.spacing(2)})`,
		},
		subHeading: {
			// marginTop: theme.spacing(2),
		},
		actionButtons: {
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
		},
		saveBtn: {
			display: 'block',
			marginTop: 32,
		},
		section: {
			padding: '32px 0px',
		},
		topSection: {
			paddingTop: '0 !important',
		},
		subSection: {
			padding: '16px 0px',
		},
		deleteIcon: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		},
		productDialogPaper: {
			width: '80%',
			maxHeight: 435,
		},
		verticalSpacing: {
			margin: `${theme.spacing(2)} 0`,
		},
	}),
);

export default DinSettings;
